import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { UserPlus, Trash2, Edit3, ShieldOff, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * CashiersPanel — operator-only UI to onboard cashiers (storefront staff),
 * configure their daily load limit + cashout permission, and view today's
 * activity per cashier.
 */
const CashiersPanel = () => {
    const [cashiers, setCashiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null); // cashier object being edited
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({
        name: '', phone: '', password: '',
        daily_load_limit: 0, can_cashout: true, commission_rate_pct: 0,
    });
    const resetForm = () => setForm({ name: '', phone: '', password: '', daily_load_limit: 0, can_cashout: true, commission_rate_pct: 0 });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/operator/cashiers`, { withCredentials: true });
            setCashiers(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load cashiers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const createCashier = async () => {
        const trim = form.name.trim();
        if (!trim || !form.phone.trim() || form.password.length < 4) {
            toast.error('Name, phone, and password (4+ chars) required');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/operator/cashiers`, {
                ...form,
                name: trim,
                daily_load_limit: Number(form.daily_load_limit) || 0,
                operator_share_pct: Math.max(0, Math.min(100, Number(form.operator_share_pct_input) || 80)) / 100,
            }, { withCredentials: true });
            toast.success(`Cashier ${trim} created`);
            setCreateOpen(false);
            resetForm();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Create failed');
        } finally {
            setBusy(false);
        }
    };

    const saveEdit = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await axios.put(`${API}/operator/cashiers/${editing.id}`, {
                name: editing.name,
                daily_load_limit: Number(editing.daily_load_limit) || 0,
                can_cashout: editing.can_cashout,
                operator_share_pct: Math.max(0, Math.min(100, Number(editing.operator_share_pct_input ?? (editing.operator_share_pct || 0.80) * 100))) / 100,
                status: editing.status,
            }, { withCredentials: true });
            toast.success('Cashier updated');
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Update failed');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Remove cashier "${c.name}"? They'll no longer be able to log in.`)) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/operator/cashiers/${c.id}`, { withCredentials: true });
            toast.success(`Removed ${c.name}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="cashiers-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-100">Cashiers</h2>
                    <p className="text-xs text-zinc-500">
                        Storefront staff that load &amp; cash out players on your behalf.
                    </p>
                </div>
                <Button
                    onClick={() => { resetForm(); setCreateOpen(true); }}
                    size="sm"
                    className="h-8 bg-zinc-100 text-zinc-900 hover:bg-white"
                    data-testid="add-cashier-btn"
                >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add cashier
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-8 text-zinc-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
                </div>
            ) : cashiers.length === 0 ? (
                <div className="text-center py-8 text-zinc-500 text-sm" data-testid="cashiers-empty">
                    No cashiers yet. Click "Add cashier" to onboard your first storefront staff member.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Name</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Phone</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Today loaded</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Today cashed</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Daily limit</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Split (op / cashier)</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Cashout?</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Status</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {cashiers.map((c, i) => (
                                <TableRow key={c.id} className="border-zinc-800 hover:bg-zinc-900/50" data-testid={`cashier-row-${i}`}>
                                    <TableCell className="text-zinc-100 font-medium">{c.name}</TableCell>
                                    <TableCell className="text-zinc-400 font-mono text-sm">{c.phone}</TableCell>
                                    <TableCell className="text-right font-mono text-zinc-100">{fmt(c.loaded_today)}</TableCell>
                                    <TableCell className="text-right font-mono text-zinc-100">{fmt(c.cashed_today)}</TableCell>
                                    <TableCell className="text-right font-mono text-zinc-300">
                                        {c.daily_load_limit > 0 ? fmt(c.daily_load_limit) : <span className="text-zinc-500">∞</span>}
                                    </TableCell>
                                    <TableCell className="text-right font-mono" data-testid={`cashier-share-${i}`}>
                                        <span className="text-amber-300">{((c.operator_share_pct ?? 0.80) * 100).toFixed(0)}%</span>
                                        <span className="text-zinc-600 mx-2">/</span>
                                        <span className="text-cyan-300">{((1 - (c.operator_share_pct ?? 0.80)) * 100).toFixed(0)}%</span>
                                    </TableCell>
                                    <TableCell>
                                        {c.can_cashout
                                            ? <span className="text-emerald-300 text-xs font-medium">Yes</span>
                                            : <span className="text-zinc-500 text-xs font-medium">No</span>}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`text-xs font-medium ${c.status === 'active' ? 'text-emerald-300' : 'text-rose-300'}`}>
                                            {c.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-1 justify-end">
                                            <Button size="sm" variant="ghost" onClick={() => setEditing({ ...c })}
                                                className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800" data-testid={`edit-cashier-${i}`}>
                                                <Edit3 className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(c)}
                                                className="h-7 w-7 p-0 text-rose-300 hover:bg-rose-500/10" data-testid={`delete-cashier-${i}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create Cashier dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-[#121212] border-white/10" data-testid="create-cashier-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">Add Cashier</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            They'll log in with this phone + password and work from your pool.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white text-xs">Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="cashier-form-name" />
                        </div>
                        <div>
                            <Label className="text-white text-xs">Phone</Label>
                            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="cashier-form-phone" />
                        </div>
                        <div>
                            <Label className="text-white text-xs">Password</Label>
                            <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="cashier-form-password" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-end">
                            <div>
                                <Label className="text-white text-xs">Daily load limit</Label>
                                <Input type="number" min="0" step="50"
                                    value={form.daily_load_limit}
                                    onChange={e => setForm({ ...form, daily_load_limit: e.target.value })}
                                    placeholder="0 = unlimited"
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                                    data-testid="cashier-form-limit" />
                            </div>
                            <div className="flex items-center gap-2 pb-2">
                                <Switch checked={form.can_cashout} onCheckedChange={(v) => setForm({ ...form, can_cashout: v })}
                                    data-testid="cashier-form-canout" />
                                <Label className="text-white text-xs">Can cash out</Label>
                            </div>
                        </div>
                        <div>
                            <Label className="text-white text-xs">Revenue split — operator share %</Label>
                            <Input type="number" min="0" max="100" step="1"
                                value={form.operator_share_pct_input}
                                onChange={e => setForm({ ...form, operator_share_pct_input: e.target.value })}
                                placeholder="80"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                                data-testid="cashier-form-share" />
                            <p className="text-[10px] text-[#A0AAB2] mt-1">
                                Cashier collects cash from players. They pay you{' '}
                                <span className="text-amber-300 font-semibold">
                                    {Number(form.operator_share_pct_input || 80).toFixed(0)}%
                                </span>{' '}
                                of net (loads − cashouts) and keep{' '}
                                <span className="text-cyan-300 font-semibold">
                                    {(100 - Number(form.operator_share_pct_input || 80)).toFixed(0)}%
                                </span>.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={createCashier} disabled={busy}
                            className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="confirm-create-cashier">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create cashier'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Cashier dialog */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="bg-[#121212] border-white/10" data-testid="edit-cashier-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">Edit {editing?.name}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3 py-2">
                            <div>
                                <Label className="text-white text-xs">Name</Label>
                                <Input value={editing.name}
                                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="edit-cashier-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-3 items-end">
                                <div>
                                    <Label className="text-white text-xs">Daily load limit ($)</Label>
                                    <Input type="number" min="0" step="50"
                                        value={editing.daily_load_limit}
                                        onChange={e => setEditing({ ...editing, daily_load_limit: e.target.value })}
                                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                                        data-testid="edit-cashier-limit" />
                                </div>
                                <div className="flex items-center gap-2 pb-2">
                                    <Switch checked={editing.can_cashout}
                                        onCheckedChange={(v) => setEditing({ ...editing, can_cashout: v })}
                                        data-testid="edit-cashier-canout" />
                                    <Label className="text-white text-xs">Can cash out</Label>
                                </div>
                            </div>
                            <div>
                                <Label className="text-white text-xs">Revenue split — operator share %</Label>
                                <Input type="number" min="0" max="100" step="1"
                                    value={editing.operator_share_pct_input ?? ((editing.operator_share_pct ?? 0.80) * 100)}
                                    onChange={e => setEditing({ ...editing, operator_share_pct_input: e.target.value })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                                    data-testid="edit-cashier-share" />
                                <p className="text-[10px] text-[#A0AAB2] mt-1">
                                    Cashier pays you{' '}
                                    <span className="text-amber-300 font-semibold">
                                        {Number(editing.operator_share_pct_input ?? (editing.operator_share_pct ?? 0.80) * 100).toFixed(0)}%
                                    </span>{' '}
                                    of net (loads − cashouts), keeps{' '}
                                    <span className="text-cyan-300 font-semibold">
                                        {(100 - Number(editing.operator_share_pct_input ?? (editing.operator_share_pct ?? 0.80) * 100)).toFixed(0)}%
                                    </span>.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={() => setEditing({ ...editing, status: editing.status === 'active' ? 'suspended' : 'active' })}
                                    variant="outline"
                                    className={`border-white/10 ${editing.status === 'active' ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}
                                    data-testid="edit-cashier-toggle-status"
                                >
                                    {editing.status === 'active'
                                        ? <><ShieldOff className="w-4 h-4 mr-2" /> Suspend cashier</>
                                        : <><ShieldCheck className="w-4 h-4 mr-2" /> Reactivate</>}
                                </Button>
                                <p className="text-xs text-[#A0AAB2]">
                                    {editing.status === 'active' ? 'Currently active' : 'Currently suspended'}
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={saveEdit} disabled={busy}
                            className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="confirm-edit-cashier">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashiersPanel;
